import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fmtDMY } from "@/lib/format";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BedDouble, Loader2, Upload } from "lucide-react";

function Choice({ checked, onClick, label, testid, danger }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
        checked ? (danger ? "border-rose-500 bg-rose-600 text-white" : "border-indigo-600 bg-indigo-600 text-white") : "bg-card hover:bg-secondary"
      }`}
    >
      {label}
    </button>
  );
}

function ConflictRow({ c, d, onChange }) {
  const { t } = useTranslation();
  const departing = c.kind === "departing";
  const set = (patch) => onChange({ ...d, ...patch });
  return (
    <div className="space-y-2 rounded-md border bg-card p-3 text-sm" data-testid={`stay-decision-${c.room_number}`}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
        <span className="font-display text-base font-extrabold">{c.room_number}</span>
        <span className="text-muted-foreground">{c.guest_name || t("Guest")}</span>
        <span className="text-xs text-muted-foreground">{fmtDMY(c.check_in)} → {fmtDMY(c.check_out)}</span>
      </div>
      <p className="text-xs text-muted-foreground">
        {departing
          ? t("The CSV has a departure clean for this room, but a long-stay guest is booked here until {{date}}.", { date: fmtDMY(c.check_out) })
          : t("A long stay starts in this room today. Is the room and stay length still right?")}
      </p>
      <div className="flex flex-wrap gap-1.5">
        <Choice checked={d.action === "keep"} onClick={() => set({ action: "keep" })} testid={`stay-decision-keep-${c.room_number}`}
          label={departing ? t("Keep long stay (normal departure)") : t("Yes, unchanged")} />
        <Choice checked={d.action === "move"} onClick={() => set({ action: "move" })} testid={`stay-decision-move-${c.room_number}`}
          label={t("Guest moved to another room")} />
        {!departing && (
          <Choice checked={d.action === "dates"} onClick={() => set({ action: "dates" })} testid={`stay-decision-dates-${c.room_number}`}
            label={t("Change check-out")} />
        )}
        <Choice danger checked={d.action === "end" || d.action === "remove"} onClick={() => set({ action: departing ? "end" : "remove" })}
          testid={`stay-decision-end-${c.room_number}`} label={departing ? t("Guest has left — end stay") : t("Cancel this long stay")} />
      </div>
      {(d.action === "move" || d.action === "dates") && (
        <div className="grid gap-2 sm:grid-cols-2">
          {d.action === "move" && (
            <div className="space-y-1">
              <Label className="text-xs">{t("New room")}</Label>
              <Input value={d.to_room || ""} onChange={(e) => set({ to_room: e.target.value })} className="h-8" data-testid={`stay-decision-room-${c.room_number}`} />
            </div>
          )}
          <div className="space-y-1">
            <Label className="text-xs">{t("Check-out")}</Label>
            <Input type="date" min={c.check_in} value={d.check_out || c.check_out} onChange={(e) => set({ check_out: e.target.value })} className="h-8" data-testid={`stay-decision-checkout-${c.room_number}`} />
          </div>
        </div>
      )}
    </div>
  );
}

export function StayCsvDecisionsDialog({ conflicts, date, pending, onClose, onConfirm }) {
  const { t } = useTranslation();
  const [dec, setDec] = useState({});
  useEffect(() => {
    if (conflicts) setDec(Object.fromEntries(conflicts.map((c) => [c.stay_id, { action: "keep" }])));
  }, [conflicts]);
  const ready = (conflicts || []).every((c) => {
    const d = dec[c.stay_id] || {};
    return d.action !== "move" || (d.to_room || "").trim();
  });
  const confirm = () => {
    const out = {};
    for (const c of conflicts) {
      const d = dec[c.stay_id] || { action: "keep" };
      out[c.stay_id] = { action: d.action === "dates" ? "keep" : d.action, to_room: (d.to_room || "").trim() || undefined, check_out: d.check_out || undefined };
    }
    onConfirm(out);
  };
  return (
    <Dialog open={!!conflicts} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-xl overflow-y-auto" data-testid="stay-decisions-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><BedDouble className="h-5 w-5 text-indigo-700" /> {t("Check long stays before importing")}</DialogTitle>
          <DialogDescription>
            {t("Nothing has been changed yet. Confirm what should happen to each long stay for {{date}}, then the CSV will be imported.", { date: fmtDMY(date) })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {(conflicts || []).map((c) => (
            <ConflictRow key={c.stay_id} c={c} d={dec[c.stay_id] || { action: "keep" }} onChange={(d) => setDec((cur) => ({ ...cur, [c.stay_id]: d }))} />
          ))}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="stay-decisions-cancel">{t("Cancel import")}</Button>
          <Button onClick={confirm} disabled={!ready || pending} className="gap-2" data-testid="stay-decisions-confirm">
            {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {t("Confirm & import")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
